import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserContext } from '../../hooks/useUserContext';
import type { SystemRole } from '../../types/organization.types';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface RoleGuardProps {
  children: ReactNode;
  /** Required system role to access this content */
  role: SystemRole;
  /** Custom fallback component when access is denied */
  fallback?: ReactNode;
  /** Redirect path when access is denied (overrides fallback) */
  redirectTo?: string;
}

/**
 * Guard component that protects routes/components based on system role.
 * Use for admin-only routes.
 * 
 * Usage:
 * <RoleGuard role="admin">
 *   <AdminDashboard />
 * </RoleGuard>
 */
export function RoleGuard({ 
  children, 
  role, 
  fallback, 
  redirectTo 
}: RoleGuardProps) {
  const { isLoading, isSystemAdmin, systemRole } = useUserContext();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Checking Access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              Verifying permissions...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has required role
  const hasAccess = role === 'admin' ? isSystemAdmin : systemRole === role;

  if (!hasAccess) {
    // Redirect if path specified
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Show custom fallback
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default access denied UI
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted-foreground py-8">
              <p className="mb-2">You do not have permission to access this page.</p>
              <p className="text-sm">
                {role === 'admin' 
                  ? 'Admin privileges are required.' 
                  : `Role "${role}" is required.`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}





